'use client';

import { useEffect, useRef } from 'react';
import useSWR from 'swr';
import { useAuth } from './auth';
import { useToast } from '../components/Toast';
import { API_BASE } from './api';

type AppNotification = {
  id: number;
  timestamp: number;
  eventType: string;
  title: string;
  message: string;
};

/**
 * Polls /api/notifications/recent for new app notifications and shows them as toasts.
 * Only active for authenticated users (admins see all, but any authenticated user
 * can poll — backend returns only notifications stored in the in-memory buffer).
 *
 * Poll interval: 10 seconds. Uses a `since` ref to only fetch notifications newer
 * than the last seen one, avoiding duplicate toasts.
 */
export function useNotificationPoll() {
  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const sinceRef = useRef<number>(Date.now());
  const shownIdsRef = useRef<Set<number>>(new Set());

  const { data } = useSWR<AppNotification[]>(
    isAuthenticated ? '/api/notifications/recent' : null,
    (url) => {
      const since = sinceRef.current;
      const sep = url.includes('?') ? '&' : '?';
      return fetch(`${API_BASE}${url}${sep}since=${since}`, {
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      }).then((r) => (r.ok ? r.json() : []));
    },
    {
      refreshInterval: 30_000,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshWhenHidden: false,
    }
  );

  useEffect(() => {
    if (!data || data.length === 0) return;
    let maxTs = sinceRef.current;
    for (const n of data) {
      if (n.timestamp > maxTs) maxTs = n.timestamp;
      if (!shownIdsRef.current.has(n.id)) {
        shownIdsRef.current.add(n.id);
        showToast(`${n.title} — ${n.message}`, 'info');
      }
    }
    if (maxTs > sinceRef.current) sinceRef.current = maxTs;
  }, [data, showToast]);
}
